import { BrButton, BrInput, BrSelect, Col, Row } from '@govbr-ds/react-components';
import styles from './baseDeCalculoFormItemNCM.module.scss';

import { useEffect, useState } from 'react';
import { ICodigoNcm as ICodigoNcmBaseCalculo } from '../../../../../../../../../interfaces/IBaseDeCalculo';
import { ICodigoNcm } from '../../../../../../../../../interfaces/ICodigoNcm';
import { formatCurrency, removeFormatCurrency } from '../../../../../../../../../utils/formatCurrency';
import { useNotificacao } from '../../../../../../../../../hooks/useNotificacao';
import NCMViewContainerList from './components/ncmViewContainerList/ncmViewContainerList';
import { useBackEndRequests } from '../../../../../../../lib/backEndRequests';
import { useQuery } from '@tanstack/react-query';
import InfoCircleTooltip from '../../../../../../../../../components/infoCircleTooltip/infoCircleTooltip';
import { IErrorWithSentryId } from 'interfaces/IErrorWithSentryId';

interface IComponentesBaseDeCalculoFormItemNCM {
  listaComponentesBaseCalculo?: ICodigoNcmBaseCalculo[];
  setListaComponentesBaseCalculo?: React.Dispatch<React.SetStateAction<ICodigoNcmBaseCalculo[]>>;
  listaCodigosNCM: ICodigoNcmBaseCalculo[];
  setListaCodigosNCM: React.Dispatch<React.SetStateAction<ICodigoNcmBaseCalculo[]>>;
  codigosNCM: ICodigoNcm[];
  type: 'Operação';
  isConsolidado?: boolean;
  isReadOnly?: boolean;
  idVendaDireta?: number | undefined;
  idRepasseParceiro?: number | undefined;
  unidadesMedida: {
    label: string;
    value: string;
  }[];
}

function ComponentesBaseDeCalculoFormItemNCM({
  listaComponentesBaseCalculo,
  setListaComponentesBaseCalculo,
  listaCodigosNCM,
  setListaCodigosNCM,
  codigosNCM,
  type,
  isConsolidado = false,
  isReadOnly,
  idRepasseParceiro,
  idVendaDireta,
  unidadesMedida,
}: Readonly<IComponentesBaseDeCalculoFormItemNCM>) {
  const [quantidade, setQuantidade] = useState('');
  const [valor, setValor] = useState('');
  const [codigoNCM, setCodigoNCM] = useState('');
  const [unidadeMedida, setUnidadeMedida] = useState('');
  const [isRejeito, setIsRejeito] = useState(false);
  const mostrarNotificacao = useNotificacao();
  const [vendaDiretaItem, setVendaDiretaItem] = useState<ICodigoNcmBaseCalculo>();
  const [repasseParceiroItem, setRepasseParceiroItem] = useState<ICodigoNcmBaseCalculo>();
  const [refreshQuery, setRefreshQuery] = useState(false);
  const [edicaoNCM, setEdicaoNCM] = useState<ICodigoNcmBaseCalculo>();

  const {
    postCadastrarVendaDiretaItem,
    putEditarVendaDiretaItem,
    postCadastrarRepasseParceiroItem,
    putEditarRepasseParceiroItem,
  } = useBackEndRequests();

  const { refetch: refetchPostPutVendaDiretaItemData, error: errorPostPutVendaDiretaItemData } = useQuery<
    ICodigoNcmBaseCalculo,
    IErrorWithSentryId
  >({
    queryKey: ['postPutCadastrarVendaDiretaItem', vendaDiretaItem],
    queryFn: async () => {
      if (vendaDiretaItem && vendaDiretaItem.id === 0) {
        return await postCadastrarVendaDiretaItem(vendaDiretaItem);
      } else if (vendaDiretaItem && vendaDiretaItem.id !== 0) {
        return await putEditarVendaDiretaItem(vendaDiretaItem);
      }
      throw errorPostPutVendaDiretaItemData;
    },
    refetchOnWindowFocus: false,
    enabled: false,
    retry: false,
  });

  const { refetch: refetchPostPutRepasseParceiroItemData, error: errorPostPutRepasseParceiroItemData } = useQuery<
    ICodigoNcmBaseCalculo,
    IErrorWithSentryId
  >({
    queryKey: ['postPutCadastrarRepasseParceiroItem', repasseParceiroItem],
    queryFn: async () => {
      if (repasseParceiroItem && repasseParceiroItem.id === 0) {
        return await postCadastrarRepasseParceiroItem(repasseParceiroItem);
      } else if (repasseParceiroItem && repasseParceiroItem.id !== 0) {
        return await putEditarRepasseParceiroItem(repasseParceiroItem);
      }
      throw errorPostPutRepasseParceiroItemData;
    },
    refetchOnWindowFocus: false,
    enabled: false,
    retry: false,
  });

  useEffect(() => {
    if (!!edicaoNCM && edicaoNCM.id !== 0) {
      setCodigoNCM(edicaoNCM.codigoNCM);
      setQuantidade(edicaoNCM.quantidade);
      setValor(formatCurrency(edicaoNCM.valor));
      setUnidadeMedida(edicaoNCM.unidadeMedida || '');
      setIsRejeito(edicaoNCM.isRejeito || false);
      if (edicaoNCM.idVendaDireta) {
        setVendaDiretaItem(edicaoNCM);
      } else {
        setRepasseParceiroItem(edicaoNCM);
      }
    }
  }, [edicaoNCM]);

  useEffect(() => {
    if (!!listaComponentesBaseCalculo && !!setListaComponentesBaseCalculo) {
      const novaLista = [...listaComponentesBaseCalculo];
      if (novaLista.length === 0) {
        setListaComponentesBaseCalculo(novaLista);
      }
    }
  }, [listaComponentesBaseCalculo, setListaComponentesBaseCalculo]);

  useEffect(() => {
    if (idVendaDireta) {
      setVendaDiretaItem({
        id: edicaoNCM?.id || 0,
        idVendaDireta: idVendaDireta,
        codigoNCM: codigoNCM,
        quantidade: quantidade,
        valor: removeFormatCurrency(valor).toString(),
        unidadeMedida: unidadeMedida,
        isRejeito: isRejeito,
      });
    } else if (idRepasseParceiro) {
      setRepasseParceiroItem({
        id: edicaoNCM?.id || 0,
        idRepasse: idRepasseParceiro,
        codigoNCM: codigoNCM,
        quantidade: quantidade,
        valor: removeFormatCurrency(valor).toString(),
        unidadeMedida: unidadeMedida,
        isRejeito: isRejeito,
      });
    }
  }, [idVendaDireta, idRepasseParceiro, edicaoNCM, codigoNCM, quantidade, valor, unidadeMedida, isRejeito]);

  const adicionarNCM = async () => {
    const QUANTIDADE_MINIMA = 0.0001;
    const QUANTIDADE_MAXIMA = 1e11;

    if (!codigoNCM || !quantidade || !valor || !unidadeMedida) {
      mostrarNotificacao({
        tipo: 'danger',
        conteudo: 'Preencha todos os campos referentes ao NCM.',
      });
      return;
    }

    if (parseFloat(quantidade) < QUANTIDADE_MINIMA) {
      mostrarNotificacao({
        tipo: 'danger',
        conteudo: `Valor mínimo para quantidade é de ${QUANTIDADE_MINIMA}.`,
      });
      return;
    }

    if (parseFloat(quantidade) >= QUANTIDADE_MAXIMA) {
      mostrarNotificacao({
        tipo: 'danger',
        conteudo: `Valor máximo para quantidade é de ${QUANTIDADE_MAXIMA}.`,
      });
      return;
    }

    if (
      listaCodigosNCM.filter((item) => item.codigoNCM === codigoNCM).length > 0 &&
      (!edicaoNCM || edicaoNCM.codigoNCM !== codigoNCM)
    ) {
      mostrarNotificacao({
        tipo: 'danger',
        conteudo: `Já existe um código NCM ${codigoNCM} na lista.`,
      });
      return;
    }

    if (idVendaDireta) {
      const retornoPostPutVendaDiretaItem = await refetchPostPutVendaDiretaItemData();
      if (retornoPostPutVendaDiretaItem.error) {
        mostrarNotificacao({
          tipo: 'danger',
          conteudo: 'Não foi possível salvar o item da Venda Direta.',
          ...(errorPostPutVendaDiretaItemData && { complementoSentryId: errorPostPutVendaDiretaItemData }),
        });
        return;
      } else {
        if (retornoPostPutVendaDiretaItem.data) {
          const existeCodigoNCM = listaCodigosNCM.some((item) => item.id === retornoPostPutVendaDiretaItem.data.id);

          if (!existeCodigoNCM) {
            setListaCodigosNCM([
              ...listaCodigosNCM,
              {
                id: retornoPostPutVendaDiretaItem.data.id,
                idVendaDireta: retornoPostPutVendaDiretaItem.data.idVendaDireta,
                codigoNCM: retornoPostPutVendaDiretaItem.data.codigoNCM,
                quantidade: retornoPostPutVendaDiretaItem.data.quantidade,
                valor: removeFormatCurrency(valor).toString(),
                unidadeMedida: unidadeMedida,
                isRejeito: retornoPostPutVendaDiretaItem.data.isRejeito,
              },
            ]);
          }
          setEdicaoNCM(undefined);
        }
      }
    }
    if (idRepasseParceiro) {
      const retornoPostPutRepasseParceiroItem = await refetchPostPutRepasseParceiroItemData();
      if (retornoPostPutRepasseParceiroItem.error) {
        mostrarNotificacao({
          tipo: 'danger',
          conteudo: 'Não foi possível salvar o item do Repasse Parceiro',
          ...(errorPostPutRepasseParceiroItemData && { complementoSentryId: errorPostPutRepasseParceiroItemData }),
        });
        return;
      } else {
        if (retornoPostPutRepasseParceiroItem.data) {
          setListaCodigosNCM([
            ...listaCodigosNCM,
            {
              id: retornoPostPutRepasseParceiroItem.data.id,
              idRepasse: retornoPostPutRepasseParceiroItem.data.idRepasse,
              codigoNCM: retornoPostPutRepasseParceiroItem.data.codigoNCM,
              quantidade: retornoPostPutRepasseParceiroItem.data.quantidade,
              valor: removeFormatCurrency(valor).toString(),
              unidadeMedida: unidadeMedida,
              isRejeito: retornoPostPutRepasseParceiroItem.data.isRejeito,
            },
          ]);
          setEdicaoNCM(undefined);
        }
      }
    }

    setRefreshQuery(true);
    setCodigoNCM('');
    setQuantidade('');
    setValor('');
    setIsRejeito(false);
  };

  const styleContainer = isConsolidado ? styles.containerSemBorda : styles.container;

  return (
    <div className={styleContainer}>
      {!isReadOnly && (
        <NCMViewContainerList
          idVendaDireta={idVendaDireta}
          idRepasseParceiro={idRepasseParceiro}
          refreshQuery={refreshQuery}
          setRefreshQuery={setRefreshQuery}
          setEdicaoNCM={setEdicaoNCM}
          edicaoNCM={edicaoNCM}
          setListaCodigosNCM={setListaCodigosNCM}
        />
      )}
      <div className={styles.componentesTitle}>Informe o Código NCM</div>
      <div className={styles.form}>
        <form id='ncmForm' className={styles.form}>
          <Row className={styles.row}>
            <Col>
              <span className={styles.label}>Código NCM</span>
              {isReadOnly ? (
                <BrInput disabled={isReadOnly} />
              ) : (
                <BrSelect
                  key={codigoNCM}
                  className={styles.select}
                  onChange={(value: string) => setCodigoNCM(value)}
                  options={codigosNCM.map((item: ICodigoNcm) => ({
                    label: `${item.codigo} - ${item.descricao}`,
                    value: item.codigo,
                  }))}
                  value={codigoNCM}
                  disabled={isReadOnly}
                />
              )}
            </Col>
            <Col>
              <span className={styles.label}>Quantidade</span>
              <BrInput
                autoComplete='off'
                required
                numeric={{
                  decimalScale: 2,
                  fixedDecimalScale: false,
                }}
                onChange={(e) => {
                  setQuantidade(e.target.value.replace(',', '.'));
                }}
                value={quantidade}
                disabled={isReadOnly}
              />
            </Col>

            <Col>
              <span className={styles.label}>Valor de {type}</span>
              <BrInput
                autoComplete='off'
                numeric={{
                  prefix: 'R$ ',
                  thousandSeparator: '.',
                  decimalScale: 2,
                  fixedDecimalScale: false,
                }}
                required
                onChange={(e) => setValor(e.target.value)}
                value={valor}
                disabled={isReadOnly}
              />
            </Col>
          </Row>
          <Row>
            <Col>
              <span className={styles.label}>Uni. de Medida</span>
              {isReadOnly ? (
                <BrInput disabled={isReadOnly} />
              ) : (
                <BrSelect
                  className={styles.select}
                  options={unidadesMedida}
                  value={unidadeMedida}
                  placeholder={unidadeMedida}
                  onChange={(value: string) => setUnidadeMedida(value)}
                />
              )}
            </Col>
            <Col className={styles.precoUnitario}>
              <span className={styles.label}>Preço Unitário</span>
              <p className={styles.precoUnitarioText}>
                {valor && quantidade
                  ? formatCurrency((removeFormatCurrency(valor) / parseFloat(quantidade)).toFixed(2))
                  : 'R$ 00,00'}
              </p>
            </Col>
            <Col>
              <div className={styles.checkBoxLabelContainer}>
                <span className={styles.label}>Rejeitos/Estéreis</span>
                <InfoCircleTooltip
                  text='Informação vinculada à redução da alíquota da CFEM em 50%, conforme § 7º, do Art. 6º, da Lei nº 7.990/1989. Os procedimentos para aproveitamento de rejeitos e estéreis estão dispostos na RESOLUÇÃO ANM Nº 85, DE 2 DE DEZEMBRO DE 2021.'
                  id='rejeitos'
                />
              </div>

              <div
                className={`br-switch icon ${styles.switch}`}
                onClick={() => setIsRejeito(!isRejeito)}
                onKeyDown={() => setIsRejeito(!isRejeito)}
                role='switch'
                aria-checked={isRejeito}
                tabIndex={0}
              >
                {isReadOnly ? (
                  <label className={styles.labelSwitch}>Sim/Não</label>
                ) : (
                  <>
                    <input id='switch-icon' type='checkbox' name='switch-icon' checked={isRejeito} role='switch' />
                    <label className={styles.labelSwitch}>{isRejeito ? 'Sim' : 'Não'}</label>
                  </>
                )}
              </div>
            </Col>
          </Row>
          <div className={styles.botao}>
            <BrButton
              onClick={adicionarNCM}
              secondary
              className={styles.botaoTerceario}
              size='small'
              disabled={isReadOnly}
            >
              <i className={`fas fa-plus fa-md  ${styles.columnIcon}`} />
              <span>{!edicaoNCM ? 'Adicionar Código NCM' : 'Editar Código NCM'}</span>
            </BrButton>
          </div>
        </form>
      </div>
    </div>
  );
}

export default ComponentesBaseDeCalculoFormItemNCM;
